import { PaymentIntentResult, SetupIntent, Stripe } from '@stripe/stripe-js';
import type { TFunction } from 'i18next';
import {
  ePlanCode,
  ePlanFrequency,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import {
  CENTS_TO_DOLLARS_CONVERSION_FACTOR,
  CURRENCY_UNITS_TO_CENTS_FACTOR,
} from './SubscriptionManager.constants';
import { tSubscriptionSummary } from 'src/providers/SubscriptionProvider/Subscription.types';
import {
  tAvailableProduct,
  tFormatPriceOptions,
  tInvoicePreviewRequest,
  tPaymentMethod,
  tPaymentMethodDefaultOutcome,
  tPaymentMethodDefaultRefusal,
  tPaymentMethodDefaultReport,
  tPlan,
  tRedirectReturnSnapshot,
  tRelatedProduct,
  tSetupIntentReturn,
} from './SubscriptionManager.types';
import { tCreditProductsOption } from './components/SubscriptionCheckout/CheckoutSummary/CheckoutSummary.types';
import { eProductType } from './SubscriptionManager.enums';
import { isValidUUID } from 'src/utils/isValidUUID';
import {
  resolveBillingErrorMessage,
  stripeJsErrorToBillingPayload,
} from 'src/utils/resolveBillingError';
import { isEmpty, isString, upperFirst } from 'lodash';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum';

/**
 * Formats a price value (in cents) into a human-readable string with currency.
 *
 * @param options - Configuration object for price formatting
 * @param options.price - The price value in cents (will be converted to dollars). If 0 or undefined, returns "$0" or "0" depending on includeDollarSign
 * @param options.currency - Optional currency code to append after the formatted price (e.g., "USD", "MXN")
 * @param options.includeDollarSign - Optional flag to include/exclude the "$" symbol at the beginning. Defaults to `true`. Set to `false` to remove the dollar sign.
 *
 * @returns Formatted price string. Examples:
 * - `formatPrice({ price: 1000 })` → `"$10.00"`
 * - `formatPrice({ price: 1000, currency: "USD" })` → `"$10.00 USD"`
 * - `formatPrice({ price: 1000, currency: "USD", includeDollarSign: false })` → `"10.00 USD"`
 * - `formatPrice({ price: 0 })` → `"$0"`
 * - `formatPrice({ price: 0, includeDollarSign: false })` → `"0"`
 *
 * @example
 * ```typescript
 * // Basic usage with dollar sign (default)
 * formatPrice({ price: 5000, currency: 'USD' }) // Returns: "$50.00 USD"
 *
 * // Without dollar sign
 * formatPrice({ price: 5000, currency: 'USD', includeDollarSign: false }) // Returns: "50.00 USD"
 *
 * // Price only, no currency
 * formatPrice({ price: 2500 }) // Returns: "$25.00"
 *
 * // Zero price
 * formatPrice({ price: 0, currency: 'MXN' }) // Returns: "$0 MXN"
 * ```
 */
export function formatPrice(options: tFormatPriceOptions) {
  const { price, currency, includeDollarSign = true } = options;

  if (price === 0 || price === undefined) {
    const dollarSign = includeDollarSign ? '$' : '';
    return currency ? `${dollarSign}0 ${currency}` : `${dollarSign}0`;
  }

  const normalizedPrice = price / CENTS_TO_DOLLARS_CONVERSION_FACTOR;
  const formattedPrice = normalizedPrice.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const dollarSign = includeDollarSign ? '$' : '';
  return currency
    ? `${dollarSign}${formattedPrice} ${currency}`
    : `${dollarSign}${formattedPrice}`;
}

/**
 * Normalize a coupon's discount value into the unit the rest of the checkout uses.
 *
 * The billing service returns `discount_value` in currency-native units for
 * fixed_amount coupons (e.g. `100` for "100 MXN off") and as a raw percentage
 * for percentage coupons (e.g. `50` for "50% off"). The checkout math
 * (`base - discount`) and `formatPrice` both treat numbers as cents — using
 * the raw fixed_amount value would short-change the discount by 100x.
 *
 * Returns a number in cents for fixed_amount, untouched percentage otherwise.
 * `null` / `undefined` inputs return `null` so callers can treat "no coupon"
 * uniformly.
 */
export function normalizeCouponDiscountValue(
  discountValue: number | null | undefined,
  discountType: 'percentage' | 'fixed_amount' | null | undefined,
): number | null {
  if (discountValue === null || discountValue === undefined) return null;
  if (discountType === 'fixed_amount') {
    return discountValue * CURRENCY_UNITS_TO_CENTS_FACTOR;
  }
  return discountValue;
}

// Helper function to extract payment intent data
export const extractPaymentIntentData = (result: PaymentIntentResult) => {
  const paymentIntent = result?.paymentIntent ?? result?.error?.payment_intent;
  return {
    id: paymentIntent?.id ?? '',
    status: paymentIntent?.status ?? '',
  };
};
export function getFrequencyInMonths(
  frequency: ePlanFrequency | string,
): number {
  // Normalize to uppercase to handle enum values ('Monthly', 'Quarterly', etc.) and string variations
  const normalizedFrequency = frequency.toUpperCase();

  switch (normalizedFrequency) {
    case 'MONTHLY':
      return 1;
    case 'QUARTERLY':
      return 3;
    case 'BIANNUAL':
      return 6;
    case 'ANNUALLY':
      return 12;
    default:
      return 1;
  }
}

export function getPlanName(planCode: ePlanCode | string): string {
  const planMap: Record<string, string> = {
    [ePlanCode.PLAN_BASIC]: 'Basic',
    [ePlanCode.PLAN_PROFESSIONAL]: 'Professional',
    [ePlanCode.PLAN_ENTERPRISE]: 'Advanced',
    [ePlanCode.PLAN_LEGACY]: 'Legacy',
  };
  return planMap[planCode] || 'Unknown';
}

/**
 * Resolves the "Total a pagar hoy" amount shown in the checkout summary.
 *
 * Source-of-truth precedence:
 *   1. `invoicePreview.total_amount` from the backend when present and ≥ 0
 *      (Stripe's prorated answer; 0 is a legitimate downgrade outcome).
 *   2. `planToBuy.trialCost` when the flow is a trial purchase.
 *   3. `totalCheckoutPrice` as a no-sub-active fallback.
 *
 * Any active coupon discount is applied last and clamped at 0.
 *
 * Extracted from `CheckoutSummary` so the precedence (and especially the
 * "backend-returned 0 is legitimate, not the RET-1105 bug" case) is testable
 * without rendering the component.
 */
export function computeTotalAmount(args: {
  invoiceTotalAmount: number | undefined;
  isTrial: boolean;
  trialCost: number | undefined;
  totalCheckoutPrice: number;
  appliedCouponDiscount: number | null;
  couponDiscountType: 'percentage' | 'fixed_amount' | null | undefined;
}): number {
  const {
    invoiceTotalAmount,
    isTrial,
    trialCost,
    totalCheckoutPrice,
    appliedCouponDiscount,
    couponDiscountType,
  } = args;

  // Treat trialCost === 0 the same way we treat invoiceTotalAmount === 0:
  // a legitimate "no charge today" value, not a falsy fallback trigger.
  // Without this, a free-trial plan (trialCost: 0) would render the full
  // recurring price instead of $0.
  //
  // RET-1173: clamp `invoiceTotalAmount < 0` to 0 via `Math.max`. A negative
  // total means Stripe accrued more credit (unused-time on the old plan +
  // extras) than the prorated new-plan charge — the customer owes $0 today,
  // with the magnitude carried forward as credit on the next invoice. Pre-fix
  // the `>= 0` check fell through to `totalCheckoutPrice` (catalog price),
  // which displayed the customer as owing the full plan price when they
  // actually owed $0. The credit copy itself is rendered at the call site
  // (`CheckoutSummary`) where the raw negative is still available via
  // `invoicePreview.data.data.total_amount`.
  // `Number.isFinite` excludes NaN / Infinity / -Infinity. The previous
  // two-arm `>= 0 / < 0` check happened to short-circuit NaN (both checks
  // return false) and fell through to the trial / catalog fallback. The
  // collapsed `Math.max(0, NaN)` returns NaN, which would propagate into
  // `formatPrice` and render as the literal string "NaN" in the UI.
  // Keep the safe fallback behavior explicit.
  const base =
    invoiceTotalAmount !== undefined && Number.isFinite(invoiceTotalAmount)
      ? Math.max(0, invoiceTotalAmount)
      : isTrial && trialCost !== undefined && trialCost >= 0
        ? trialCost
        : totalCheckoutPrice;

  if (appliedCouponDiscount !== null && couponDiscountType === 'percentage') {
    return Math.max(0, base * (1 - appliedCouponDiscount / 100));
  }
  if (appliedCouponDiscount !== null && couponDiscountType === 'fixed_amount') {
    return Math.max(0, base - appliedCouponDiscount);
  }

  return base;
}

/**
 * Decides whether `fetchInvoicePreview` should call the backend or just reset
 * its local state. Extracted as a pure function so the regression for
 * RET-1105 (synthetic $0 shortcut for "same plan, different period") is
 * testable without rendering the entire `useSubscriptionManager` hook.
 *
 * Returns `'fetch'` only when there's a real upgrade Stripe needs to price:
 * the user has an active subscription AND the target plan differs from the
 * current one in plan code, frequency, or both. Every other case returns
 * `'reset'` so the caller clears `invoicePreview` and the UI falls back to
 * `totalCheckoutPrice`.
 */
export function shouldFetchInvoicePreview(args: {
  hasWorkspaceId: boolean;
  hasPlanToBuy: boolean;
  isClient: boolean;
  subscriptionStatus: eSubscriptionStatus | string | undefined;
  subscriptionPlanCode: ePlanCode | string | undefined;
  subscriptionFrequency: ePlanFrequency | string | undefined;
  planToBuyPlanCode: ePlanCode | string | undefined;
  planToBuyFrequency: ePlanFrequency | string | undefined;
}): 'fetch' | 'reset' {
  if (!args.hasWorkspaceId || !args.hasPlanToBuy) return 'reset';

  // Preview's own status gate (RET-1105): no prorate preview for unpaid/dormant subs.
  const isActiveStatus =
    args.subscriptionStatus === eSubscriptionStatus.ACTIVE ||
    args.subscriptionStatus === eSubscriptionStatus.TRIAL ||
    args.subscriptionStatus === eSubscriptionStatus.PAST_DUE ||
    args.subscriptionStatus === eSubscriptionStatus.SCHEDULED_CANCELLATION;

  if (!args.isClient || !isActiveStatus) return 'reset';

  const isNoOpUpgrade =
    args.subscriptionPlanCode === args.planToBuyPlanCode &&
    args.subscriptionFrequency === args.planToBuyFrequency;

  if (isNoOpUpgrade) return 'reset';

  return 'fetch';
}

/**
 * Builds the invoice-preview request payload.
 *
 * Extracted from `fetchInvoicePreview` (`SubscriptionManager.hook.ts`) so this payload is
 * testable without rendering `useSubscriptionManager`, which consumes Stripe, the router and
 * four more context hooks. It earns the extraction: this payload prices EVERY plan change that
 * reaches the checkout, not only the periodicity ones.
 *
 * `tInvoicePreviewRequest` carries no frequency field. The only channel a frequency reaches
 * leadbilling through is a suffix baked into each `product_id` (`_MONTHLY_`, `_QUARTERLY_`, …),
 * which is why one resolved frequency feeds all five product lines below.
 */
export function buildInvoicePreviewRequest(args: {
  planToBuy: tPlan;
  subscriptionFrequency: string;
  subscriptionCurrency: string;
  subscriptionPlanCode: string;
  extraSeats: number;
  watermarkRemoval: boolean;
  isWalletEnabled: boolean;
  monthlyCredits: tCreditProductsOption | null;
  selectedAddonProducts: tRelatedProduct[];
}): tInvoicePreviewRequest {
  const {
    planToBuy,
    subscriptionFrequency,
    subscriptionCurrency,
    subscriptionPlanCode,
    extraSeats,
    watermarkRemoval,
    isWalletEnabled,
    monthlyCredits,
    selectedAddonProducts,
  } = args;

  // RET-1944 (AC2): the DESTINATION frequency the customer selected, not the one they are
  // leaving. Quoting the current frequency prices a periodicity change at the old rate — and,
  // once leadbilling classifies frequency-only changes, hides the change from it entirely,
  // since the request would read as "no frequency change at all".
  //
  // The `??` looks redundant against `tPlan.frequencyName`, which the type declares required.
  // It is deliberate: this value comes from the plan-catalog API, so the type is a promise the
  // wire can break, and the real upgrade-write request already carries the same fallback
  // (`planToBuy?.frequencyName ?? subscription.frequency`). Same ambiguity, same rule.
  const frequency = (
    planToBuy.frequencyName ?? subscriptionFrequency
  ).toUpperCase();

  const request: tInvoicePreviewRequest = {
    products: [
      {
        product_id: `${planToBuy.planCode}_${frequency}_${subscriptionCurrency}`,
        quantity: 1,
      },
    ],
  };

  // Add all extra seats that the upgraded plan will have
  if (extraSeats > 0) {
    const seatProduct = `EXTRA_SEAT_${planToBuy.planCode
      .split('_')[1]
      .toUpperCase()}_${frequency}_${subscriptionCurrency}`;

    request.products.push({
      product_id: seatProduct,
      quantity: extraSeats + planToBuy.includedSeats,
    });
  }

  // Add watermark removal if upgrading from basic plan and watermark removal is enabled
  const isUpgradeFromBasic = subscriptionPlanCode === ePlanCode.PLAN_BASIC;
  if (isUpgradeFromBasic && watermarkRemoval) {
    request.products.push({
      product_id: `LEADBOT_WATERMARK_${frequency}_${subscriptionCurrency}`,
      quantity: 1,
    });
  }

  // Add monthly credits if selected and different from base
  if (
    !isWalletEnabled &&
    monthlyCredits &&
    monthlyCredits.price > 0 &&
    isValidUUID(monthlyCredits.value ?? '')
  ) {
    const monthlyCreditsProduct = planToBuy.relatedProducts.find(
      (product) => product.uuid === monthlyCredits.value,
    );

    if (monthlyCreditsProduct) {
      // Replace the period part (e.g., ANNUALLY, MONTHLY) with the new plan's period
      const updatedCode = monthlyCreditsProduct.code.replace(
        /_(MONTHLY|QUARTERLY|BIANNUAL|ANNUALLY|WEEKLY|TRIAL)_/,
        `_${frequency}_`,
      );

      request.products.push({
        product_id: updatedCode,
        quantity: 1,
      });
    }
  }

  // Add selected addon products
  if (selectedAddonProducts.length > 0) {
    selectedAddonProducts.forEach((addonProduct) => {
      const addonProductType =
        addonProduct.productType === eProductType.LEADBOT_PREMIUM
          ? 'LEADBOT_WATERMARK'
          : addonProduct.productType;

      request.products.push({
        product_id: `${addonProductType}_${frequency}_${subscriptionCurrency}`,
        quantity: 1,
      });
    });
  }

  return request;
}

/**
 * Determines whether a "Reactivate" action should revert a scheduled
 * cancellation in place instead of opening the plan-selection flow.
 *
 * A subscription that is still active but pending cancellation only needs the
 * scheduled cancellation removed, so it can be reactivated directly. A fully
 * cancelled (expired) subscription has no active period to restore and must go
 * through plan selection again.
 *
 * @param subscription - Current subscription summary (status + pending flag)
 * @returns `true` when the cancellation can be reverted in place; `false` when
 * the plan-selection flow is required.
 */
export function shouldReactivateInPlace(
  subscription: Pick<tSubscriptionSummary, 'status' | 'pendingCancellation'>,
): boolean {
  return (
    subscription.pendingCancellation &&
    subscription.status !== eSubscriptionStatus.CANCELLED
  );
}

// true ↔ a status the leadbilling upgrade path accepts (ACTIVE / TRIAL /
// SCHEDULED_CANCELLATION). Excludes PAST_DUE & NO_PAID (UpgradeSubscriptionUseCase
// throws SubscriptionPastDueException — they belong to the pay-balance flow) and
// CANCELLED/INACTIVE/DELETED/PAUSED (no live sub → create). See
// docs/solutions/logic-errors/router-gates-on-workspace-flag-not-subscription-status.md
export function hasUpgradeableSubscription(
  status: eSubscriptionStatus | string | undefined,
): boolean {
  return (
    status === eSubscriptionStatus.ACTIVE ||
    status === eSubscriptionStatus.TRIAL ||
    status === eSubscriptionStatus.SCHEDULED_CANCELLATION
  );
}

// true when there is an upgradeable subscription and the picked plan/frequency
// differs — routes the pay action to update-subscription instead of create.
export function shouldUseUpgradeContext(args: {
  subscriptionStatus: eSubscriptionStatus | string | undefined;
  subscriptionPlanCode: ePlanCode | string | undefined;
  subscriptionFrequency: ePlanFrequency | string | undefined;
  hasPlanToBuy: boolean;
  planToBuyPlanCode: ePlanCode | string | undefined;
  planToBuyFrequency: ePlanFrequency | string | undefined;
}): boolean {
  return (
    hasUpgradeableSubscription(args.subscriptionStatus) &&
    args.hasPlanToBuy &&
    (args.subscriptionPlanCode !== args.planToBuyPlanCode ||
      args.subscriptionFrequency !== args.planToBuyFrequency)
  );
}

/**
 * Decides which card the payment method list should end up selected on.
 *
 * Priority: the card the user just added, then the provider default, then the
 * current selection while it still exists, then the first available card.
 *
 * A card that fails a charge stays the provider default, so resolving by
 * default alone sent the user back to the failing card right after they added
 * a new one — and the retry charged that same card. `preferredPaymentMethodId`
 * is how the add-card flow claims the card it just attached.
 *
 * @returns the card to select, or `undefined` when the customer has none.
 */
export function resolvePaymentMethodSelection(args: {
  paymentMethods: tPaymentMethod[];
  defaultPaymentMethodId?: string;
  currentSelectionId?: string;
  preferredPaymentMethodId?: string;
}): tPaymentMethod | undefined {
  const findById = (paymentMethodId?: string) =>
    isEmpty(paymentMethodId)
      ? undefined
      : args.paymentMethods.find(
          (method) => method.paymentMethodId === paymentMethodId,
        );

  return (
    findById(args.preferredPaymentMethodId) ??
    findById(args.defaultPaymentMethodId) ??
    findById(args.currentSelectionId) ??
    args.paymentMethods[0]
  );
}

/**
 * Pulls the payment method id out of a confirmed SetupIntent.
 *
 * Stripe returns `payment_method` either as a bare id or as an expanded object
 * depending on the intent's expansion, so both shapes have to be handled.
 *
 * @returns the attached card id, or `undefined` when the intent carries none.
 */
export function extractSetupIntentPaymentMethodId(
  setupIntent?: SetupIntent,
): string | undefined {
  const paymentMethod = setupIntent?.payment_method;

  if (isString(paymentMethod)) {
    return paymentMethod;
  }

  return paymentMethod?.id;
}

/**
 * Whether a Stripe.js error is one the customer can correct in the card form.
 *
 * The add-card modal used to close on ANY `confirmSetup` error, which threw the
 * whole form away: someone who mistyped three CVC digits had to re-enter the
 * card number, the expiry and the billing address before they could try again.
 * That is how one workspace ended up adding three cards in eleven minutes.
 *
 * Stripe already draws exactly this distinction on the error itself.
 * `card_error` (wrong CVC, expired card, bad number, decline) and
 * `validation_error` (an incomplete field) are the two the PaymentElement can
 * both explain inline and let the customer fix in place, so the modal stays
 * open for those. Anything else - `api_error`, `invalid_request_error`, a
 * misconfigured intent - cannot be fixed by editing the form, so the modal
 * still closes and the alert carries the message on its own.
 *
 * @returns `true` when the modal should stay open for an in-place correction.
 */
export function isStripeErrorCorrectableInForm(error?: {
  type?: string;
}): boolean {
  return error?.type === 'card_error' || error?.type === 'validation_error';
}

/**
 * `text` as a closed sentence. The decline map mixes copy that ends in a
 * period with copy that does not («La tarjeta ha sido declinada»), and
 * something is about to be said right after it.
 */
function closeSentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * The alert for a card billing refused to make the default.
 *
 * RET-2347 — its brand cannot be charged in the customer's currency.
 * Billing names the brand and the currency with Stripe's codes (`amex`,
 * `usd`), which are not words a customer reads. Each one resolves to its name
 * in the locale when it has one, and degrades to something still legible when
 * it does not: the brand with its first letter capitalised (`Visa`), the
 * currency as its upper-cased code (`EUR`). A refusal that arrives without
 * either one says the same thing without naming them, instead of printing a
 * sentence with a hole in it.
 *
 * One title for both places a card is made the default from, the add-card form
 * and the saved-cards list, and it does not say the card was not saved. The
 * add-card form does try to take the card back off the account, but billing
 * will not delete a customer's only card, so that is not ours to promise.
 * That the card cannot be used is true either way.
 *
 * RET-2155 — the bank refused it when billing set it on the subscription.
 * The reason is the bank's, so it is resolved by the same map as a card the
 * bank declines in the add-card form (`resolveBillingErrorMessage`, fed the
 * way a Stripe.js error is): one decline code reads the same wherever it
 * happens. What follows it depends on what billing confirmed. With
 * `previousCardRestored` it put the previous card back everywhere, so nothing
 * the customer pays with has changed, and the notice says so. Without it a
 * subscription may be left on the refused card: the notice says that going
 * back did not work and where to choose another card as preferred.
 *
 * `t` MUST be bound to `settings/SubscriptionManager`, which owns this copy.
 */
export function resolvePaymentMethodDefaultRefusalNotice(
  refusal: tPaymentMethodDefaultRefusal,
  t: TFunction,
): { type: eAlertType; title: string; message: string } {
  if (refusal.status === 'paymentMethodDeclined') {
    const reason = resolveBillingErrorMessage(
      stripeJsErrorToBillingPayload({
        code: 'card_declined',
        decline_code: refusal.declineCode ?? undefined,
      }),
      t,
    );

    return {
      type: eAlertType.ERROR,
      title: t('paymentMethodNotices.declined.title'),
      // "Your default card did not change" is said only when billing confirmed
      // it put the previous card back everywhere. When it could not, a
      // subscription is left on the card the bank just refused, so the notice
      // says so and sends the customer to pick another card as preferred.
      message: t(
        refusal.previousCardRestored
          ? 'paymentMethodNotices.declined.body'
          : 'paymentMethodNotices.declined.bodyNotRestored',
        { reason: closeSentence(reason) },
      ),
    };
  }

  const title = t('paymentMethodNotices.brandNotSupported.title');

  if (!refusal.brand || !refusal.currency) {
    return {
      type: eAlertType.ERROR,
      title,
      message: t('paymentMethodNotices.brandNotSupported.bodyWithoutDetail'),
    };
  }

  return {
    type: eAlertType.ERROR,
    title,
    message: t('paymentMethodNotices.brandNotSupported.body', {
      brand: t(`paymentMethodNotices.brands.${refusal.brand.toLowerCase()}`, {
        defaultValue: upperFirst(refusal.brand),
      }),
      currency: t(
        `paymentMethodNotices.currencies.${refusal.currency.toLowerCase()}`,
        { defaultValue: refusal.currency.toUpperCase() },
      ),
    }),
  };
}

/**
 * Single source of truth for "does the loaded catalog have a product of this
 * type, and which entry" — RET-2032.
 *
 * `availableForPurchase` and `allRelatedProducts` are two independent
 * responses from the same backend call (`SubscriptionManagerService` /
 * `tiers/products`) with no guaranteed subset relationship — a product type
 * can be present in one and absent from the other. Before this resolver,
 * four call sites each re-implemented the lookup on their own, with
 * different precedence and different null/case tolerance, and could disagree
 * about the very same catalog:
 *   - the "Add more users" modal's price / enabled-button logic checked
 *     `availableForPurchase` first, falling back to `allRelatedProducts`;
 *   - `updateSeatsPlan`'s actual purchase target checked only
 *     `allRelatedProducts`;
 *   - the "Agrega más usuarios" card's visibility gate (`filteredAddOns`)
 *     checked only `allRelatedProducts`, with a case-sensitive match;
 *   - `getExtraSeatsPrice` (the price that same card actually displays)
 *     checked only `availableForPurchase`, with a case-sensitive match.
 * That combination let the button render enabled from one list and then
 * throw on click because the purchase only searched the other list — or let
 * the card's gate paint it (case-insensitive, both lists) while its price
 * stayed on the case-sensitive/single-list lookup and quoted $0 for a
 * catalog the modal would still charge the real price against. Routing
 * every "is SEATS in the catalog" / "which SEATS product" question through
 * this one function makes all four decisions structurally unable to
 * disagree.
 *
 * Tolerant to a dirty catalog entry with a missing/null `productType`
 * elsewhere in either array, and case-insensitive (some environments send the
 * value lowercase) — neither tolerance may regress (RET-2032 guardrail 4).
 */
export function findCatalogProduct(
  products:
    | {
        availableForPurchase?: tAvailableProduct[];
        allRelatedProducts?: tRelatedProduct[];
      }
    | null
    | undefined,
  productType: string,
): tRelatedProduct | tAvailableProduct | undefined {
  const matchesType = (product: { productType?: string | null }) =>
    (product.productType ?? '').toLowerCase() === productType.toLowerCase();

  return (
    products?.availableForPurchase?.find(matchesType) ??
    products?.allRelatedProducts?.find(matchesType)
  );
}

/**
 * Boolean sibling of {@link findCatalogProduct} for visibility gates (e.g.
 * "does the catalog have a SEATS product, so the add-on card should show").
 * Same precedence and tolerance — see that function's docblock.
 */
export function hasCatalogProduct(
  products:
    | {
        availableForPurchase?: tAvailableProduct[];
        allRelatedProducts?: tRelatedProduct[];
      }
    | null
    | undefined,
  productType: string,
): boolean {
  return findCatalogProduct(products, productType) !== undefined;
}

/**
 * RET-1923 (D1/D2) — the i18n key for the success notice `addPaymentMethod`
 * shows once the newly-added card was promoted to the account's default
 * payment method.
 *
 * It is the positive mirror of `messages.paymentMethodNotSetAsDefault` (the
 * WARNING already fired from the `makePaymentMethodDefault` catch): same
 * consequence — the card now also governs plan renewal — stated in the
 * opposite direction. Deliberately a *different* string, not the same
 * sentence reused, so the mirror stays a mirror in both directions.
 */
export const PAYMENT_METHOD_ADDED_SET_AS_DEFAULT_KEY =
  'messages.paymentMethodAddedSetAsDefault';

/**
 * RET-2348 — the TITLE of that same notice: «Tarjeta guardada».
 *
 * The notice used to travel without one, so the toast fell back to the generic
 * SUCCESS title, «Operación exitosa». Raised from the wallet's top-up modal
 * right after a declined charge, that title read as the PAYMENT going through,
 * while nothing had been charged: saving a card only saves it. The case is
 * told once, on `cardSaved` in `TopUpModal.connected.tsx`.
 *
 * A title of its own says which operation succeeded. It is the same reason the
 * wallet's notices carry theirs (`useWalletOutcome.ts`: the generic title
 * "dice «Operación exitosa» y no dice cuál").
 */
export const PAYMENT_METHOD_ADDED_TITLE_KEY = 'messages.paymentMethodAddedTitle';

/**
 * RET-2155 — the success notice for the same outcome when the plan has a
 * payment pending. Stripe retries a failed renewal on its own schedule, days
 * apart, so a customer who just replaced the card that failed would otherwise
 * wait for the next retry without knowing the plan can be paid right now.
 *
 * It names the button that does it, «Pagar periodo»
 * (`page.SubscriptionOverview.payPeriod`), which charges the open invoice with
 * the selected card, and the card just added is the one left selected. It
 * also says where that button is, Ajustes › Suscripción, because the add-card
 * form opens from other screens too (the wallet's top-up modal) and the
 * button may not be in front of the customer. The three labels are written
 * into the copy, and a test holds them to the strings those screens render.
 */
export const PAYMENT_METHOD_ADDED_WHILE_PAST_DUE_KEY =
  'paymentMethodNotices.addedWhilePastDue';

/**
 * RET-2428 — the success notice when billing saved the card WITHOUT making it
 * the default: the workspace is in a trial the customer has not confirmed
 * (`T-2`). «Tu tarjeta se guardó. Cuando confirmes tu plan, podrás elegirla
 * para cobrarlo.» It must not say the card became the default, because it did
 * not: the plan will be charged to the card chosen at confirmation.
 *
 * Shown by both paths that call "make default": adding a card and «Establecer
 * como preferida». Same title as every add, «Tarjeta guardada».
 */
export const PAYMENT_METHOD_SAVED_WITHOUT_DEFAULT_KEY =
  'paymentMethodNotices.savedWithoutDefault';

/**
 * Whether the plan has a payment pending, the one «Pagar periodo» charges.
 *
 * The subscription overview enables that button with this, and the add-card
 * success notice names the button only when this is true, so the notice never
 * points at a button that is switched off.
 */
export function isSubscriptionPastDue(
  status: eSubscriptionStatus | string | undefined,
): boolean {
  return (
    status === eSubscriptionStatus.PAST_DUE ||
    status === eSubscriptionStatus.NO_PAID
  );
}

/**
 * RET-2428 defense: what a "make default" call did with the card, once the
 * workspace's state is applied to what billing reported.
 *
 * A boolean in the 200 body is authoritative. A body that does not carry it
 * comes from a backend older than leadbilling#1345; in a trial that backend
 * keeps the card without making it the default, so saying «quedó como
 * predeterminada» there would be false. Outside a trial the same body means
 * what it always meant.
 */
export function resolvePaymentMethodDefaultOutcome(
  report: tPaymentMethodDefaultReport,
  isTrial: boolean,
): tPaymentMethodDefaultOutcome {
  if (report !== 'unreported') return report;

  return isTrial ? 'savedWithoutDefault' : 'madeDefault';
}

/**
 * Decides whether a successful `addPaymentMethod` should show a success
 * notice, and which keys it carries.
 *
 * Extracted out of `addPaymentMethod` (`SubscriptionManager.hook.ts`,
 * ~L1255-1435) so the D1/D2 decision — the happy path says nothing today,
 * and the promotion-to-default consequence is announced only on failure — is
 * testable without mounting the 3,167-line hook and its Stripe wiring.
 *
 * `defaultCallSucceeded: false` returns `null` on purpose: that case already has
 * its own alert (the WARNING in the existing `makePaymentMethodDefault`
 * catch). The two are mutually exclusive by construction — this function
 * only ever adds the SUCCESS notice for the branch that has none today.
 *
 * `defaultCallSucceeded` (named `promotedToDefault` before RET-2428) only says
 * the "make default" call answered 200. It does not say the card became the
 * default: `outcome` says that.
 *
 * `titleKey` is REQUIRED in the returned shape (RET-2348), not optional: the
 * toast paints the generic «Operación exitosa» whenever a title is missing, so
 * an optional field would let the next caller drop it and bring that title
 * back without anything failing. With it required, a notice without a title
 * does not compile.
 *
 * `pastDue` (RET-2155) swaps the sentence for the one that also says the plan
 * can be paid now. Optional and false when absent, so a caller that knows
 * nothing about the plan keeps the notice it had. The title stays «Tarjeta
 * guardada» either way: saving the card is still the only thing that happened.
 *
 * `outcome` (RET-2428) is what billing's 200 said it did with the card.
 * `savedWithoutDefault` wins over `pastDue`: both other sentences say the card
 * «quedó como predeterminada», which is false in that case. Optional and
 * `madeDefault` when absent, so a caller that does not read the body keeps the
 * notice it had.
 *
 * @returns `null` when no notice should fire.
 */
export function resolveAddPaymentMethodSuccessNotice(args: {
  defaultCallSucceeded: boolean;
  pastDue?: boolean;
  outcome?: tPaymentMethodDefaultOutcome;
}): { type: eAlertType; titleKey: string; messageKey: string } | null {
  if (!args.defaultCallSucceeded) return null;

  if (args.outcome === 'savedWithoutDefault') {
    return {
      type: eAlertType.SUCCESS,
      titleKey: PAYMENT_METHOD_ADDED_TITLE_KEY,
      messageKey: PAYMENT_METHOD_SAVED_WITHOUT_DEFAULT_KEY,
    };
  }

  return {
    type: eAlertType.SUCCESS,
    titleKey: PAYMENT_METHOD_ADDED_TITLE_KEY,
    messageKey: args.pastDue
      ? PAYMENT_METHOD_ADDED_WHILE_PAST_DUE_KEY
      : PAYMENT_METHOD_ADDED_SET_AS_DEFAULT_KEY,
  };
}

/**
 * Generic retry-until-condition helper — no React, no Stripe.
 *
 * RET-1923 (D3): `addPaymentMethod` used to wait a single fixed 2000ms
 * before re-reading the payment-methods list, then used whatever it found
 * regardless of whether the newly-attached card had actually landed on
 * Stripe's side yet. When Stripe took longer than that one window, the card
 * silently never showed up as selected.
 *
 * Calls `operation`, and while `isDone(result)` is false and attempts remain,
 * waits `intervalMs` (a real timer — callers under fake timers must advance
 * them) and calls it again. Always resolves with the LAST result obtained,
 * whether or not `isDone` ever returned true — a budget that never finds
 * what it's looking for must still resolve, not hang.
 *
 * D3.1 (RET-1923, checker vuelta 2): a single attempt REJECTING (a request
 * that truly failed, not "the card isn't there yet") used to abort the whole
 * retry and burn the rest of the budget — the exact same visible symptom as
 * the fixed-2000ms bug this ticket exists to kill (an added card that never
 * shows up selected). Each attempt is now caught individually and treated
 * like a not-done result: retry with whatever budget remains. The LAST
 * attempt is the one that decides the outcome, not "did every attempt fail":
 * when it rejects — there's no more-recent result to fall back to, only an
 * error — this re-throws that error, so the caller's own try/catch around
 * this call (see `addPaymentMethod`) reports it exactly as it already does
 * today for a re-read that fails outright. When the last attempt resolves,
 * its result is what comes back, even if `isDone` never said yes and even if
 * an earlier attempt in the same run had rejected — falling back to that
 * older result instead would silently reproduce the bug this ticket exists
 * to kill.
 */
export async function retryUntil<T>(args: {
  operation: () => Promise<T>;
  isDone: (result: T) => boolean;
  intervalMs: number;
  maxAttempts: number;
}): Promise<T> {
  const { operation, isDone, intervalMs, maxAttempts } = args;

  const attempt = async (): Promise<{ result: T } | { error: unknown }> => {
    try {
      return { result: await operation() };
    } catch (error) {
      return { error };
    }
  };

  let outcome = await attempt();
  let attempts = 1;

  while (
    attempts < maxAttempts &&
    ('error' in outcome || !isDone(outcome.result))
  ) {
    await new Promise<void>((resolve) => setTimeout(resolve, intervalMs));
    outcome = await attempt();
    attempts += 1;
  }

  if ('error' in outcome) {
    throw outcome.error;
  }

  return outcome.result;
}

/**
 * RET-1974: the alert for a bank redirect return whose SetupIntent could not
 * be read back from Stripe. The previous card stays selected.
 */
export const REDIRECT_RETURN_NOT_CONFIRMED_KEY =
  'paymentMethodNotices.redirectNotConfirmed';

/**
 * RET-1974: the alert for a bank redirect return where the bank did not
 * authorize the card. Nothing was saved.
 */
export const REDIRECT_RETURN_DECLINED_KEY =
  'paymentMethodNotices.redirectDeclined';

/**
 * RET-1974: the query parameters a card add that went through the bank's page
 * comes back with. Stripe appends the first four to the `return_url` (no
 * `redirect_status` in the real test-mode capture); `skip_default` is ours,
 * set on the `return_url` of an add that must not become the default.
 */
const SETUP_INTENT_RETURN_PARAMS: readonly string[] = [
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
  'source_type',
  'skip_default',
];

/**
 * RET-1974: what the URL says about a card add that came back from the bank's
 * page. Null unless it carries `setup_intent_client_secret`: that is what
 * reads the intent back, and its absence keeps any other return (a
 * PaymentIntent's, a bare `redirect_status`) from being taken for a card.
 */
export function readSetupIntentReturn(
  params: URLSearchParams,
): tSetupIntentReturn | null {
  const clientSecret = params.get('setup_intent_client_secret');
  if (!clientSecret) return null;

  return {
    setupIntentId:
      params.get('setup_intent') ?? setupIntentIdFromClientSecret(clientSecret),
    clientSecret,
    redirectStatus: params.get('redirect_status'),
    skipDefault: params.get('skip_default') === '1',
  };
}

/** RET-1974: the same query without the parameters of a card add return. */
export function withoutSetupIntentReturn(
  params: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  SETUP_INTENT_RETURN_PARAMS.forEach((name) => next.delete(name));

  return next;
}

/** `seti_123_secret_abc` → `seti_123`. */
export function setupIntentIdFromClientSecret(clientSecret: string): string {
  return clientSecret.split('_secret_')[0];
}

function redirectReturnSnapshotKey(workspaceId: string): string {
  return `ret1974:returnSnapshot:${workspaceId}`;
}

/**
 * RET-1974 (`M-21`): keeps the cards from before an add through a bank
 * redirect, which wipes everything in memory. One key per workspace; a newer
 * add overwrites it. Storage that throws (blocked, private mode) only loses
 * the snapshot, never the add.
 */
export function writeRedirectReturnSnapshot(
  workspaceId: string,
  snapshot: tRedirectReturnSnapshot,
): void {
  try {
    sessionStorage.setItem(
      redirectReturnSnapshotKey(workspaceId),
      JSON.stringify(snapshot),
    );
  } catch {
    // Without storage there is no snapshot to keep.
  }
}

/**
 * RET-2452 (KTD-7): the snapshot `writeRedirectReturnSnapshot` kept, or `null`
 * when there is none, it cannot be read (storage that throws) or it is not a
 * snapshot. Reads only: an unreadable key is never taken for an absent one
 * that something could then write over.
 */
export function readRedirectReturnSnapshot(
  workspaceId: string,
): tRedirectReturnSnapshot | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(redirectReturnSnapshotKey(workspaceId));
  } catch {
    return null;
  }
  if (stored === null) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const setupIntentId: unknown = Reflect.get(parsed, 'setupIntentId');
    const previous: unknown = Reflect.get(parsed, 'previousPaymentMethods');
    if (
      typeof setupIntentId !== 'string' ||
      typeof previous !== 'object' ||
      previous === null
    ) {
      return null;
    }
    const plan: unknown = Reflect.get(previous, 'plan');
    const account: unknown = Reflect.get(previous, 'account');
    return {
      setupIntentId,
      previousPaymentMethods: {
        plan: typeof plan === 'string' ? plan : null,
        account: typeof account === 'string' ? account : null,
      },
    };
  } catch {
    return null;
  }
}

/**
 * RET-1974: drops the snapshot, only when it belongs to `setupIntentId`. A
 * snapshot of another add (another tab, a seat purchase) is not this one's.
 */
export function clearRedirectReturnSnapshot(
  workspaceId: string,
  setupIntentId: string,
): void {
  try {
    const key = redirectReturnSnapshotKey(workspaceId);
    const stored = sessionStorage.getItem(key);
    if (stored === null) return;
    const snapshot = JSON.parse(stored) as Partial<tRedirectReturnSnapshot>;
    if (snapshot.setupIntentId === setupIntentId) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Without storage there is no snapshot to drop.
  }
}

/**
 * RET-1974: reads a SetupIntent back from Stripe, or `undefined` when it
 * cannot be read: Stripe answered `{ error }`, the call rejected, or it took
 * longer than `timeoutMs`. Stripe.js sets no timeout of its own, and the bank
 * return holds its guards until this ends, so it gets one. An answer that
 * arrives after the timeout is dropped.
 */
export async function retrieveSetupIntentWithin({
  stripe,
  clientSecret,
  timeoutMs,
}: {
  stripe: Stripe;
  clientSecret: string;
  timeoutMs: number;
}): Promise<SetupIntent | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), timeoutMs);
  });
  const retrieved = stripe.retrieveSetupIntent(clientSecret).then(
    (result) => (result.error ? undefined : result.setupIntent),
    () => undefined,
  );

  try {
    return await Promise.race([retrieved, timedOut]);
  } finally {
    clearTimeout(timer);
  }
}

